import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Copyright, 
  Globe, 
  DollarSign, 
  LineChart, 
  Headphones, 
  Music, 
  Clock,
  Link2,
  YoutubeIcon,
} from "lucide-react";

interface ServiceCardProps {
  title: string;
  image: string;
  category: string;
  tags: string[];
  description?: string;
  icon?: React.ReactNode;
}

const ServiceCard = ({ title, image, category, tags, description, icon }: ServiceCardProps) => {
  return (
    <div className="group relative overflow-hidden">
      <img 
        src={image} 
        alt={title} 
        className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110"
      />
      
      <div className="absolute inset-0 flex flex-col justify-between p-6 z-10">
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, tagIndex) => (
            <span key={tagIndex} className="tag-pill bg-black/40 text-white border border-white/20">
              {tag}
            </span>
          ))}
        </div>
        
        <div>
          <div className="ophira-section-label inline-flex mb-3">{category}</div>
          <h3 className="text-2xl font-bold text-white">{title}</h3>
          {description && (
            <p className="text-white/80 mt-2 max-w-md">{description}</p>
          )}
        </div>
      </div>
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
      
      {icon && (
        <div className="absolute top-6 right-6 w-12 h-12 rounded-xl bg-ophira-yellow/10 ring-1 ring-ophira-yellow/30 flex items-center justify-center backdrop-blur-sm shadow-[0_8px_20px_rgba(0,0,0,0.25)]">
          {icon}
        </div>
      )}
    </div>
  );
};

const TextServiceCard = ({ title, description, icon }: { title: string; description: string; icon: React.ReactNode }) => {
  return (
    <div className="relative bg-ophira-darkgray/30 p-8 rounded-xl transition-all duration-300 hover:bg-ophira-darkgray/50 border border-ophira-yellow/10">
      <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-ophira-yellow/60 to-transparent rounded-l-xl" />
      <div className="flex items-start gap-5 mb-4 group">
        <div className="w-12 h-12 rounded-xl bg-ophira-yellow/10 flex items-center justify-center group-hover:bg-ophira-yellow/20 transition-colors duration-300">
          {icon}
        </div>
        <h3 className="text-2xl font-bold text-white tracking-tight group-hover:text-ophira-yellow transition-colors duration-300 pt-1">{title}</h3>
      </div>
      <p className="text-white/65 leading-relaxed">{description}</p>
      <div className="absolute bottom-0 left-0 w-0 h-1 bg-ophira-yellow group-hover:w-full transition-all duration-500" />
    </div>
  );
};

const ServicesSection = () => {
  // Services data
  const services = [
    {
      title: "Music Distribution",
      image: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1674&q=80",
      category: "STREAMING",
      tags: ["Spotify", "Apple Music", "Amazon"],
      icon: <Music size={24} className="text-black" />,
    },
    {
      title: "Global Reach",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80",
      category: "DISTRIBUTION",
      tags: ["150+ Platforms", "Worldwide", "Expanded Reach"],
      icon: <Globe size={24} className="text-black" />,
    },
    {
      title: "100% Ownership",
      image: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80",
      category: "MONETIZATION",
      tags: ["Keep 100%", "Subscription Model", "No Hidden Fees"],
      icon: <DollarSign size={24} className="text-black" />,
    },
    {
      title: "Advanced Analytics",
      image: "/uploads/analytics2.jpg",
      category: "INSIGHTS",
      tags: ["Detailed", "Performance Tracking", "Data-Driven"],
      icon: <LineChart size={24} className="text-black" />,
    },
    {
      title: "Playlist Promotion",
      image: "https://images.unsplash.com/photo-1504509546545-e000b4a62425?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      category: "MARKETING",
      tags: ["Featured Playlists", "Increased Visibility", "Strategic Placement"],
      icon: <Headphones size={24} className="text-black" />,
    },
    {
      title: "Copyright Registration",
      image: "/uploads/hand-shake.jpg",
      category: "LICENSING",
      tags: ["Song", "Video", "Film & TV"],
      icon: <Copyright size={24} className="text-black" />,
    },
    // {
    //   title: "Team Management Access",
    //   image: "/uploads/650fce86-123d-4806-a9b5-d8a6ffdb76cf.png",
    //   category: "MANAGEMENT",
    //   tags: ["Team Access", "Controlled", "Account Management"],
    //   description: "Grant controlled access to your team to assist with managing your YouAux account.",
    //   icon: <Users size={24} className="text-black" />,
    // },
    // {
    //   title: "Copyright Registration",
    //   image: "/uploads/ff41ca73-da36-41f9-9d1f-ee9630f44ab3.png",
    //   category: "LEGAL",
    //   tags: ["U.S. Copyright Office", "Certificate", "Protection"],
    //   description: "Easily copyright your sound recordings and musical compositions. Receive a physical certificate in the mail from the U.S. Copyright Office.",
    //   icon: <Copyright size={24} className="text-black" />,
    // },
    // {
    //   title: "YouTube Content ID",
    //   image: "/uploads/fa328886-3a38-471a-bd66-87a892fcbebd.png",
    //   category: "MONETIZATION",
    //   tags: ["Automated", "Collection", "Revenue"],
    //   description: "Register your music with YouTube Content ID to automatically collect money anytime your music is used in a YouTube video.",
    //   icon: <Youtube size={24} className="text-black" />,
    // },
  ];

  const additionalServices = [
    {
      title: "24 Hour Release",
      description: "Get your music released within 24 hours across all major platforms.",
      icon: <Clock size={24} className="text-white/80" />,
    },
    {
      title: "Official Artist Channel",
      description: "Get your own official artist channel on YouTube.",
      icon: <YoutubeIcon size={24} className="text-white/80" />,
    },
    {
      title: "Pre-Save Link",
      description: "Create a pre-save link for your music to increase your reach and grow your audience organically.",
      icon: <Link2 size={24} className="text-white/80" />,
    },
    {
      title: "VEVO Distribution",
      description: "Get your music distributed to VEVO to increase your reach and grow your audience organically.",
      icon: <img src="/uploads/vevo.png" alt="VEVO" className="w-8 h-8 object-contain" />,
    },
    // {
    //   title: "Global Reach",
    //   description: "Distribute to over 150+ platforms worldwide including Spotify, Apple Music, Amazon Music, TIDAL, YouTube Music and more.",
    //   icon: <Globe size={24} className="text-black" />,
    // },
    // {
    //   title: "100% Ownership",
    //   description: "Keep all your royalties with our subscription model. No revenue share, no hidden fees, just transparent pricing.",
    //   icon: <DollarSign size={24} className="text-black" />,
    // }
  ];

  return (
    <section className="py-20 bg-black">
      <div className="container mx-auto px-4">
        
        <div className="flex flex-col lg:flex-row items-start justify-between gap-16 mb-16">
          <div className="w-full lg:w-1/2">
            <div className="ophira-section-label">OUR SERVICES</div>
            <h2 className="ophira-section-title">What We Offer</h2>
          </div>
          <div className="w-full lg:w-1/2">
            <p className="text-white/65 mb-10 max-w-xl">
              We provide all the tools and services independent artists need to succeed in today's competitive music industry.
            </p>
            <Link to="/services">
              <Button className="ophira-button">
                Explore Music Distribution & Marketing Services <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
        
        <div className="mt-24 mb-8">
          <div className="ophira-section-label">ADDITIONAL SERVICES</div>
          <h3 className="text-3xl font-bold text-white mb-8">More Tools For Your Success</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {additionalServices.map((service, index) => (
            <TextServiceCard 
              key={index} 
              title={service.title} 
              description={service.description} 
              icon={service.icon} 
            />
          ))}
        </div>
        
        {/* <div className="mt-16 flex justify-center">
          <Link to="/pricing">
            <Button className="ophira-button">
              View Pricing Plans <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div> */}
      </div>
    </section>
  );
};

export default ServicesSection;
